import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Form,
  Input,
  Button,
  Space,
  Row,
  Col,
  DatePicker,
  Tag,
  Tooltip,
  message,
  Modal,
  Card,
  Tabs,
  Popconfirm,
} from "antd";
import type { TableProps } from "antd";
import request from "../../../utils/request";
import {
  HotWordItem,
  HotWordBlacklistItem,
  TabProps,
} from "../../../types/dataControl";
import { DeleteOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { TextArea } = Input;

const HotWordTab: React.FC<TabProps> = ({ API_BASE_URL, token }) => {
  // 热词列表相关状态
  const [hotWordList, setHotWordList] = useState<HotWordItem[]>([]);
  const [hotWordLoading, setHotWordLoading] = useState<boolean>(false);
  const [hotWordTotal, setHotWordTotal] = useState<number>(0);
  const [hotWordPagination, setHotWordPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  // 黑名单相关状态
  const [blacklistData, setBlacklistData] = useState<HotWordBlacklistItem[]>(
    []
  );
  const [blacklistLoading, setBlacklistLoading] = useState<boolean>(false);
  const [blacklistTotal, setBlacklistTotal] = useState<number>(0);
  const [blacklistPagination, setBlacklistPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  // 模态框状态
  const [blacklistModalVisible, setBlacklistModalVisible] =
    useState<boolean>(false);
  const [editingBlacklist, setEditingBlacklist] =
    useState<HotWordBlacklistItem | null>(null);

  const [form] = Form.useForm();
  const [blacklistForm] = Form.useForm();

  // 获取热词列表
  const fetchHotWordList = useCallback(async () => {
    if (!token) return;

    setHotWordLoading(true);
    try {
      const values = form.getFieldsValue();
      const params: any = {
        page: hotWordPagination.current,
        page_size: hotWordPagination.pageSize,
      };

      // 添加筛选条件
      if (values.open_id) params.open_id = values.open_id;
      if (values.summary_id) params.summary_id = values.summary_id;
      if (values.word) params.word = values.word;
      if (values.project_key) params.project_key = values.project_key;

      // 处理时间范围
      if (values.dateRange && values.dateRange[0] && values.dateRange[1]) {
        params.start_time = values.dateRange[0].format("YYYY-MM-DD");
        params.end_time = values.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await request.get<{
        code: number;
        msg: string;
        data: { total: number; list: HotWordItem[] };
      }>(`${API_BASE_URL}/api/v3/admin/hotword/list`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setHotWordList(response.data.data.list);
        setHotWordTotal(response.data.data.total);
      } else {
        message.error(response.data.msg || "获取热词列表失败");
      }
    } catch (error) {
      console.error("获取热词列表失败:", error);
      message.error("获取热词列表失败，请稍后重试");
    } finally {
      setHotWordLoading(false);
    }
  }, [API_BASE_URL, token, form, hotWordPagination]);

  // 获取黑名单列表
  const fetchBlacklistData = useCallback(async () => {
    if (!token) return;

    setBlacklistLoading(true);
    try {
      const params = {
        page: blacklistPagination.current,
        page_size: blacklistPagination.pageSize,
      };

      const response = await request.get<{
        code: number;
        msg: string;
        data: { total: number; list: HotWordBlacklistItem[] };
      }>(`${API_BASE_URL}/api/v3/admin/hotword/blacklist/list`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setBlacklistData(response.data.data.list);
        setBlacklistTotal(response.data.data.total);
      } else {
        message.error(response.data.msg || "获取黑名单列表失败");
      }
    } catch (error) {
      console.error("获取黑名单列表失败:", error);
      message.error("获取黑名单列表失败，请稍后重试");
    } finally {
      setBlacklistLoading(false);
    }
  }, [API_BASE_URL, token, blacklistPagination]);

  // 初始加载数据
  useEffect(() => {
    fetchHotWordList();
  }, [fetchHotWordList]);

  useEffect(() => {
    fetchBlacklistData();
  }, [fetchBlacklistData]);

  // 处理热词表格变化
  const handleHotWordTableChange: TableProps<HotWordItem>["onChange"] = (
    pagination
  ) => {
    setHotWordPagination({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 20,
    });
  };

  // 处理黑名单表格变化
  const handleBlacklistTableChange: TableProps<HotWordBlacklistItem>["onChange"] =
    (pagination) => {
      setBlacklistPagination({
        current: pagination.current || 1,
        pageSize: pagination.pageSize || 20,
      });
    };

  // 处理热词查询
  const handleHotWordSearch = () => {
    setHotWordPagination({
      ...hotWordPagination,
      current: 1,
    });
    fetchHotWordList();
  };

  // 重置热词筛选
  const handleHotWordReset = () => {
    form.resetFields();
    setHotWordPagination({
      current: 1,
      pageSize: 20,
    });
    fetchHotWordList();
  };

  // 打开黑名单模态框
  const openBlacklistModal = (record?: HotWordBlacklistItem) => {
    setEditingBlacklist(record || null);
    if (record) {
      blacklistForm.setFieldsValue({
        word: record.word,
        reason: record.reason,
      });
    } else {
      blacklistForm.resetFields();
    }
    setBlacklistModalVisible(true);
  };

  // 保存黑名单
  const handleSaveBlacklist = async () => {
    try {
      const values = await blacklistForm.validateFields();

      if (editingBlacklist) {
        // 更新黑名单
        const response = await request.post(
          `${API_BASE_URL}/api/v3/admin/hotword/blacklist/update`,
          {
            id: editingBlacklist.id,
            word: values.word,
            reason: values.reason,
            enabled: 1, // 默认启用
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.code === 200) {
          message.success("更新黑名单成功");
          setBlacklistModalVisible(false);
          fetchBlacklistData();
        } else {
          message.error(response.data.msg || "更新黑名单失败");
        }
      } else {
        // 批量添加黑名单
        const words = values.word
          .split(/\r?\n/)
          .map((w: string) => w.trim())
          .filter((w: string) => w);
        if (words.length === 0) {
          message.warning("请输入至少一个热词");
          return;
        }
        let successCount = 0;
        let failList: string[] = [];
        for (const word of words) {
          try {
            const response = await request.post(
              `${API_BASE_URL}/api/v3/admin/hotword/blacklist/add`,
              {
                word,
                reason: values.reason,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            if (response.data.code === 200) {
              successCount++;
            } else {
              failList.push(word);
            }
          } catch (e) {
            failList.push(word);
          }
        }
        if (successCount > 0) {
          message.success(`成功添加${successCount}个黑名单词`);
          setBlacklistModalVisible(false);
          fetchBlacklistData();
        }
        if (failList.length > 0) {
          message.error(`以下热词添加失败: ${failList.join(", ")}`);
        }
      }
    } catch (error) {
      console.error("保存黑名单失败:", error);
      message.error("保存黑名单失败，请重试");
    }
  };

  // 删除黑名单
  const handleDeleteBlacklist = async (record: HotWordBlacklistItem) => {
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/admin/hotword/blacklist/delete`,
        {
          id: record.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("删除黑名单成功");
        fetchBlacklistData();
      } else {
        message.error(response.data.msg || "删除黑名单失败");
      }
    } catch (error) {
      console.error("删除黑名单失败:", error);
      message.error("删除黑名单失败，请重试");
    }
  };

  // 热词表格列定义
  const hotWordColumns: TableProps<HotWordItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: "摘要ID",
      dataIndex: "summary_id",
      key: "summary_id",
      width: 100,
    },
    {
      title: "用户ID/邮箱",
      key: "user",
      width: 180,
      render: (_, record) => (
        <div>
          <div>UID: {record.open_id}</div>
          <div>{record.email !== "NULL" ? record.email : "-"}</div>
        </div>
      ),
    },
    {
      title: "热词",
      dataIndex: "word",
      key: "word",
      width: 150,
      render: (word) => <Tag color="blue">{word}</Tag>,
    },
    {
      title: "描述",
      dataIndex: "description",
      key: "description",
      ellipsis: {
        showTitle: false,
      },
      render: (description) => (
        <Tooltip title={description}>
          <span>{description}</span>
        </Tooltip>
      ),
    },
    {
      title: "摘要标题",
      dataIndex: "summary_title",
      key: "summary_title",
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (title) => (
        <Tooltip title={title}>
          <span>{title}</span>
        </Tooltip>
      ),
    },
    {
      title: "项目Key",
      dataIndex: "project_key",
      key: "project_key",
      width: 180,
      ellipsis: true,
    },
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      width: 180,
      ellipsis: {
        showTitle: false,
      },
      render: (url) => (
        <Tooltip title={url}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        </Tooltip>
      ),
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 170,
      render: (date) => new Date(date).toLocaleString("zh-CN"),
    },
  ];

  // 黑名单表格列定义
  const blacklistColumns: TableProps<HotWordBlacklistItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: "热词",
      dataIndex: "word",
      key: "word",
      width: 150,
      render: (word) => <Tag color="red">{word}</Tag>,
    },
    {
      title: "原因",
      dataIndex: "reason",
      key: "reason",
      ellipsis: {
        showTitle: false,
      },
      render: (reason) => (
        <Tooltip title={reason}>
          <span>{reason}</span>
        </Tooltip>
      ),
    },
    {
      title: "创建者",
      dataIndex: "created_by",
      key: "created_by",
      width: 100,
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 170,
      render: (date) => new Date(date).toLocaleString("zh-CN"),
    },
    {
      title: "操作",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            onClick={() => openBlacklistModal(record)}
            style={{ padding: 0 }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这个黑名单项吗？"
            description="删除后将无法恢复"
            onConfirm={() => handleDeleteBlacklist(record)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: 0 }}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Tabs defaultActiveKey="hotwords">
        <TabPane
          tab={
            <span>
              <i className="ri-bookmark-line" style={{ marginRight: 8 }} />
              热词管理
            </span>
          }
          key="hotwords"
        >
          {/* 热词筛选表单 */}
          <Card title="筛选条件" size="small" style={{ marginBottom: 16 }}>
            <Form form={form} layout="vertical" onFinish={handleHotWordSearch}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item label="用户ID" name="open_id">
                    <Input placeholder="请输入用户ID" allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item label="摘要ID" name="summary_id">
                    <Input placeholder="请输入摘要ID" allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item label="热词" name="word">
                    <Input placeholder="请输入热词关键字" allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item label="项目Key" name="project_key">
                    <Input placeholder="请输入项目Key" allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={8}>
                  <Form.Item label="时间范围" name="dateRange">
                    <RangePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={8}>
                  <Form.Item label=" " colon={false}>
                    <Space>
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={
                          <i
                            className="ri-search-line"
                            style={{ marginRight: 4 }}
                          />
                        }
                      >
                        查询
                      </Button>
                      <Button
                        onClick={handleHotWordReset}
                        icon={
                          <i
                            className="ri-refresh-line"
                            style={{ marginRight: 4 }}
                          />
                        }
                      >
                        重置
                      </Button>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          {/* 热词列表表格 */}
          <Table
            columns={hotWordColumns}
            dataSource={hotWordList}
            rowKey="id"
            loading={hotWordLoading}
            pagination={{
              current: hotWordPagination.current,
              pageSize: hotWordPagination.pageSize,
              total: hotWordTotal,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
            }}
            onChange={handleHotWordTableChange}
            scroll={{ x: 1400 }}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              <i className="ri-forbid-line" style={{ marginRight: 8 }} />
              黑名单管理
            </span>
          }
          key="blacklist"
        >
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              onClick={() => openBlacklistModal()}
              icon={<i className="ri-add-line" style={{ marginRight: 4 }} />}
            >
              添加黑名单
            </Button>
          </div>

          {/* 黑名单列表表格 */}
          <Table
            columns={blacklistColumns}
            dataSource={blacklistData}
            rowKey="id"
            loading={blacklistLoading}
            pagination={{
              current: blacklistPagination.current,
              pageSize: blacklistPagination.pageSize,
              total: blacklistTotal,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
            }}
            onChange={handleBlacklistTableChange}
            scroll={{ x: 800 }}
          />
        </TabPane>
      </Tabs>

      {/* 黑名单编辑模态框 */}
      <Modal
        title={editingBlacklist ? "编辑黑名单" : "添加黑名单"}
        open={blacklistModalVisible}
        onOk={handleSaveBlacklist}
        onCancel={() => setBlacklistModalVisible(false)}
        destroyOnClose
      >
        <Form form={blacklistForm} layout="vertical">
          <Form.Item
            label="热词"
            name="word"
            rules={[{ required: true, message: editingBlacklist ? "请输入热词" : "请输入要加入黑名单的热词（每行一个）" }]}
          >
            {editingBlacklist ? (
              <Input placeholder="请输入要加入黑名单的热词" />
            ) : (
              <TextArea rows={6} placeholder="每行一个热词，支持批量添加" />
            )}
          </Form.Item>

          <Form.Item label="原因" name="reason">
            <TextArea rows={4} placeholder="请输入加入黑名单的原因" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HotWordTab;
